/**
 * Palette, procedural textures and the shared material set.
 *
 * THERE ARE NO ASSET FILES. Every texture in the game is generated here at
 * runtime — into a 2D canvas for the field markings, or straight into typed
 * arrays for the sprite and the environment map. That is a deliberate art
 * direction: a clean high-contrast stadium (dark shell, bright pitch, saturated
 * team accents) reads well from procedural sources, where a photoreal look
 * would not.
 *
 * Everything that computes NUMBERS (colour ramps, sprite falloff, the equirect
 * environment) is a pure function exported separately from the thing that
 * uploads it to the GPU, so the numbers can be tested in Node while the
 * `Texture` objects only ever exist in a browser.
 */

import {
  CanvasTexture,
  ClampToEdgeWrapping,
  Color,
  DataTexture,
  DoubleSide,
  EquirectangularReflectionMapping,
  FrontSide,
  FloatType,
  LineBasicMaterial,
  LinearFilter,
  LinearMipmapLinearFilter,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  RGBAFormat,
  RepeatWrapping,
  SRGBColorSpace,
  Texture,
} from 'three';
import { ARENA } from '../physics/constants';

// ---------------------------------------------------------------------------
// Palette
// ---------------------------------------------------------------------------

/**
 * Team colours, indexed by team: 0 = blue (defends -Y), 1 = orange (+Y).
 * The index order is load-bearing — it is the same order as `CarState.team`
 * and `MatchState.score`, and getting it backwards is invisible in review.
 */
export const TEAM_COLORS: readonly [number, number] = [0x2a7dff, 0xff8a1f];

/** Slightly desaturated variants for large emissive areas (nets, goal glow). */
export const TEAM_COLORS_DIM: readonly [number, number] = [0x1d4fa8, 0xa8571a];

export const PALETTE = {
  /** Pitch base. Dark enough that the white markings carry the contrast. */
  floor: 0x1a2230,
  /** Wall shell. Reads as smoked glass under the stadium lights. */
  wall: 0x0d1220,
  ceiling: 0x070a12,
  /** Structural trim: goal frames, wall seams, boost pad rims. */
  trim: 0x8fa6c8,
  marking: 0xf2f6ff,
  rubber: 0x14161c,
  /**
   * Car glazing. This is the SURFACE colour, not the darkness: the tint comes
   * from `attenuationColor` over the glass's thickness, so a near-black value
   * here does not make smoked glass, it makes a hole in the car.
   */
  glass: 0x8290a8,
  chrome: 0xb9c4d6,
  boostCore: 0xfff3d0,
  boostFlame: 0xff8a2a,
  sky: 0x0a1020,
} as const;

/** Team colour as a Three `Color`. Allocates; call it at build time, not per frame. */
export const teamColor = (team: 0 | 1): Color => new Color(TEAM_COLORS[team]);

/**
 * Which team's end a physics Y coordinate belongs to. Blue defends -Y, so the
 * blue goal is at negative Y. This is the single place that fact is encoded on
 * the render side; the goal builder and the tests both go through it.
 */
export const teamForY = (y: number): 0 | 1 => (y < 0 ? 0 : 1);

/** Goal mouth centre Y for a team, in the physics frame. */
export const goalYForTeam = (team: 0 | 1): number =>
  team === 0 ? -ARENA.halfLength : ARENA.halfLength;

// ---------------------------------------------------------------------------
// Colour ramps (pure)
// ---------------------------------------------------------------------------

/** Linear blend of two packed 0xRRGGBB colours. `t` is clamped to [0, 1]. */
export const lerpColorHex = (a: number, b: number, t: number): number => {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * k);
  const g = Math.round(ag + (bg - ag) * k);
  const bl = Math.round(ab + (bb - ab) * k);
  return (r << 16) | (g << 8) | bl;
};

/**
 * Boost flame colour by particle age, `t` in [0, 1] from birth to death:
 * white-hot core -> orange -> deep red, then it just fades out. Returned as
 * linear-ish 0..1 floats because it goes into a vertex colour attribute, which
 * Three treats as already-linear.
 */
export const boostFlameColor = (t: number): [number, number, number] => {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  const hex = k < 0.35
    ? lerpColorHex(0xfff6dd, 0xffc24a, k / 0.35)
    : lerpColorHex(0xffc24a, 0xd63a12, (k - 0.35) / 0.65);
  return [((hex >> 16) & 0xff) / 255, ((hex >> 8) & 0xff) / 255, (hex & 0xff) / 255];
};

/** Supersonic trail colour: cold white-blue that cools as it fades. */
export const supersonicColor = (t: number): [number, number, number] => {
  const hex = lerpColorHex(0xeaf4ff, 0x2f6bd4, t < 0 ? 0 : t > 1 ? 1 : t);
  return [((hex >> 16) & 0xff) / 255, ((hex >> 8) & 0xff) / 255, (hex & 0xff) / 255];
};

// ---------------------------------------------------------------------------
// Field markings
// ---------------------------------------------------------------------------

/**
 * Field marking layout, in uu. Rocket League publishes no marking dimensions —
 * these are chosen by eye against reference footage and are pure art direction,
 * unlike anything in ./arena-data.
 */
export const FIELD_MARKINGS = {
  centreCircleRadius: 940,
  centreSpotRadius: 96,
  /** Half-width of the goal box, and how far it reaches out from the goal line. */
  goalBoxHalfWidth: 1786,
  goalBoxDepth: 1024,
  /** The wide arc in front of each goal box. */
  goalArcRadius: 1400,
  lineWidth: 26,
  /** Mown-turf stripes running goal to goal. */
  stripeCount: 16,
} as const;

/** Texture resolution. 4:5 to match the 8192 x 10240 pitch, so lines stay round. */
export const FIELD_TEXTURE_SIZE = { width: 2048, height: 2560 } as const;

/**
 * Where the floodlights land on the pitch: `[x, y, radius, peak]` in the
 * PHYSICS frame. Four corner pools plus a wide centre lift, matching the four
 * corner light banks in `equirectEnvData` and the four masts the stadium builds
 * — if these three ever disagree, the reflection in the car paint will show a
 * light that is not on the pitch.
 */
export const FLOODLIGHT_POOLS: readonly (readonly [number, number, number, number])[] = [
  [-2500, -3400, 4600, 0.075],
  [2500, -3400, 4600, 0.075],
  [-2500, 3400, 4600, 0.075],
  [2500, 3400, 4600, 0.075],
  [0, 0, 5600, 0.03],
];

/**
 * Pitch UV, planar in the physics XY plane. v = 0 is the BLUE end (-Y): the
 * texture is uploaded with `flipY = false`, so v = 0 is also canvas row 0 and
 * "top of the canvas" means "blue end" everywhere below.
 */
export const fieldUv = (x: number, y: number): [number, number] => [
  (x + ARENA.halfWidth) / (2 * ARENA.halfWidth),
  (y + ARENA.halfLength) / (2 * ARENA.halfLength),
];

/** Inverse of `fieldUv` for the canvas painter: uv -> pixel. */
const uvToPx = (u: number, v: number): [number, number] => [
  u * FIELD_TEXTURE_SIZE.width,
  v * FIELD_TEXTURE_SIZE.height,
];

/** Physics XY -> canvas pixel. */
const fieldPx = (x: number, y: number): [number, number] => {
  const [u, v] = fieldUv(x, y);
  return uvToPx(u, v);
};

/** uu -> pixels along X. The two axes have the same scale by construction. */
const PX_PER_UU = FIELD_TEXTURE_SIZE.width / (2 * ARENA.halfWidth);

/**
 * Paint the pitch. Browser only — the caller must not import this at module
 * scope in a test.
 */
export const makeFieldTexture = (): CanvasTexture => {
  const canvas = document.createElement('canvas');
  canvas.width = FIELD_TEXTURE_SIZE.width;
  canvas.height = FIELD_TEXTURE_SIZE.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable');

  const w = canvas.width;
  const h = canvas.height;

  ctx.fillStyle = '#1a2230';
  ctx.fillRect(0, 0, w, h);

  // Mown stripes, running goal to goal so they lead the eye down the pitch.
  const stripeWidth = w / FIELD_MARKINGS.stripeCount;
  for (let i = 0; i < FIELD_MARKINGS.stripeCount; i++) {
    ctx.fillStyle = i % 2 === 0 ? 'rgba(255,255,255,0.028)' : 'rgba(0,0,0,0.05)';
    ctx.fillRect(i * stripeWidth, 0, stripeWidth, h);
  }

  // Team wash at each end. v = 0 is blue by construction (see `fieldUv`).
  for (const team of [0, 1] as const) {
    const goalY = goalYForTeam(team);
    const [, gy] = fieldPx(0, goalY);
    const [, iy] = fieldPx(0, goalY + (team === 0 ? 1 : -1) * 2600);
    const grad = ctx.createLinearGradient(0, gy, 0, iy);
    const c = new Color(TEAM_COLORS[team]);
    // Eased back from 0.30 when the stadium raised the pitch's overall level:
    // the wash is a multiplier on how bright the floor is, and at the new
    // exposure the orange end read as brown mud from a chase camera sitting in
    // the middle of it.
    grad.addColorStop(0, `rgba(${(c.r * 255) | 0},${(c.g * 255) | 0},${(c.b * 255) | 0},0.20)`);
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, Math.min(gy, iy), w, Math.abs(iy - gy));
  }

  const line = PX_PER_UU * FIELD_MARKINGS.lineWidth;
  ctx.strokeStyle = 'rgba(236,243,255,0.85)';
  ctx.fillStyle = 'rgba(236,243,255,0.85)';
  ctx.lineWidth = line;
  ctx.lineJoin = 'round';

  // Halfway line and centre circle.
  const [, midY] = fieldPx(0, 0);
  ctx.beginPath();
  ctx.moveTo(0, midY);
  ctx.lineTo(w, midY);
  ctx.stroke();

  const [cx, cy] = fieldPx(0, 0);
  ctx.beginPath();
  ctx.arc(cx, cy, FIELD_MARKINGS.centreCircleRadius * PX_PER_UU, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, cy, FIELD_MARKINGS.centreSpotRadius * PX_PER_UU, 0, Math.PI * 2);
  ctx.fill();

  // Goal boxes and the arc in front of them, mirrored at both ends.
  for (const team of [0, 1] as const) {
    const goalY = goalYForTeam(team);
    const inward = team === 0 ? 1 : -1;
    const boxFar = goalY + inward * FIELD_MARKINGS.goalBoxDepth;
    const [x0, y0] = fieldPx(-FIELD_MARKINGS.goalBoxHalfWidth, goalY);
    const [x1, y1] = fieldPx(FIELD_MARKINGS.goalBoxHalfWidth, boxFar);
    ctx.strokeRect(Math.min(x0, x1), Math.min(y0, y1), Math.abs(x1 - x0), Math.abs(y1 - y0));

    const [ax, ay] = fieldPx(0, boxFar);
    const r = FIELD_MARKINGS.goalArcRadius * PX_PER_UU;
    ctx.beginPath();
    // Canvas angles run clockwise from +X; the half we want is the one pointing
    // toward midfield, which is +canvas-y for blue and -canvas-y for orange.
    if (team === 0) ctx.arc(ax, ay, r, 0, Math.PI);
    else ctx.arc(ax, ay, r, Math.PI, Math.PI * 2);
    ctx.stroke();
  }

  // Corner ticks, one per 45-degree corner plane.
  ctx.lineWidth = line * 0.8;
  for (const sx of [-1, 1]) {
    for (const sy of [-1, 1]) {
      const [px, py] = fieldPx(sx * 2944, sy * 3968);
      const [qx, qy] = fieldPx(sx * 3400, sy * 4460);
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(qx, qy);
      ctx.stroke();
    }
  }

  // FLOODLIGHT POOLS, baked. Four warm overlapping ellipses centred where the
  // corner masts point, plus a broad lift over the middle.
  //
  // Baked rather than lit with four SpotLights on purpose. Spot lights are
  // per-fragment cost on EVERY material in the scene — the cars, the crowd, the
  // roof — for a gradient that never moves relative to the pitch it falls on.
  // The one thing a real light would add is a shadow, and the key light already
  // casts the only shadow that matters.
  ctx.globalCompositeOperation = 'lighter';
  for (const [px, py, radius, strength] of FLOODLIGHT_POOLS) {
    const [cxp, cyp] = fieldPx(px, py);
    const r = radius * PX_PER_UU;
    const pool = ctx.createRadialGradient(cxp, cyp, r * 0.08, cxp, cyp, r);
    // NEARLY WHITE, and weak. These are a brightness variation, not a colour:
    // four warm pools overlap across the middle of the pitch, and at any real
    // saturation what they add up to there is a brown field with the markings
    // washed out of it. Two passes were spent discovering that the acceptable
    // warmth is almost none.
    pool.addColorStop(0, `rgba(252,250,243,${strength})`);
    pool.addColorStop(0.55, `rgba(242,243,246,${strength * 0.42})`);
    pool.addColorStop(1, 'rgba(228,233,244,0)');
    ctx.fillStyle = pool;
    ctx.fillRect(0, 0, w, h);
  }
  ctx.globalCompositeOperation = 'source-over';

  // Vignette, so the pitch does not read as a flat sheet under a flat light.
  // Lighter than it was: the pools now do most of the falloff, and stacking a
  // heavy vignette on top of them crushes the corners to black.
  const vig = ctx.createRadialGradient(w / 2, h / 2, h * 0.24, w / 2, h / 2, h * 0.66);
  vig.addColorStop(0, 'rgba(0,0,0,0)');
  vig.addColorStop(1, 'rgba(0,0,0,0.34)');
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, w, h);

  const texture = new CanvasTexture(canvas);
  // See `fieldUv`: v = 0 must be canvas row 0, or the team ends swap.
  texture.flipY = false;
  texture.colorSpace = SRGBColorSpace;
  texture.wrapS = ClampToEdgeWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.anisotropy = 8;
  texture.minFilter = LinearMipmapLinearFilter;
  texture.magFilter = LinearFilter;
  return texture;
};

// ---------------------------------------------------------------------------
// The glass shell
// ---------------------------------------------------------------------------

/**
 * Tuning for `applyGlassShader`. Every field is a live uniform, so the whole
 * look can be adjusted from a console without a rebuild.
 */
export interface GlassOptions {
  /** Colour added at grazing angles. This is what "catches the light". */
  edgeColor?: number;
  /** Fresnel exponent. Higher = the rim is thinner and harder. */
  edgePower?: number;
  /** How much opacity the fresnel term adds on top of `material.opacity`. */
  edgeAlpha?: number;
  /** Mullion grid: colour, cells around the shell, cells up it. */
  gridColor?: number;
  gridU?: number;
  gridV?: number;
  gridStrength?: number;
  /** Height the vertical grid coordinate is normalized by, uu. */
  gridHeight?: number;
  /** Team wash near each goal end: strength, and the |z| band it ramps over. */
  endGlow?: number;
  endStart?: number;
  endFull?: number;
}

export interface GlassUniforms {
  uEdgeColor: { value: Color };
  uEdgePower: { value: number };
  uEdgeAlpha: { value: number };
  uGridColor: { value: Color };
  uGridU: { value: number };
  uGridV: { value: number };
  uGridHeight: { value: number };
  uGridStrength: { value: number };
  /** Team wash at +Z. Blue defends -Y in physics, which is +Z here. */
  uBlueColor: { value: Color };
  /** Team wash at -Z. */
  uOrangeColor: { value: Color };
  uEndGlow: { value: number };
  uEndStart: { value: number };
  uEndFull: { value: number };
}

const GLASS_VERTEX_HEAD = /* glsl */ `
varying vec3 vGlassWorld;
`;

const GLASS_VERTEX_BODY = /* glsl */ `
vGlassWorld = ( modelMatrix * vec4( transformed, 1.0 ) ).xyz;
`;

const GLASS_FRAGMENT_HEAD = /* glsl */ `
varying vec3 vGlassWorld;
uniform vec3 uEdgeColor;
uniform float uEdgePower;
uniform float uEdgeAlpha;
uniform vec3 uGridColor;
uniform float uGridU;
uniform float uGridV;
uniform float uGridHeight;
uniform float uGridStrength;
uniform vec3 uBlueColor;
uniform vec3 uOrangeColor;
uniform float uEndGlow;
uniform float uEndStart;
uniform float uEndFull;

// Anti-aliased unit-period line, with a fade once the cells fall below a few
// pixels. Without the fade the grid turns into flat grey haze at distance,
// which is exactly the "opaque wall" look the glass exists to avoid.
float glassGridLine( float x ) {
  float w = fwidth( x );
  float d = abs( fract( x ) - 0.5 );
  float line = smoothstep( 0.5 - clamp( w * 2.0, 0.002, 0.3 ), 0.5, d );
  return line * ( 1.0 - smoothstep( 0.08, 0.4, w ) );
}
`;

const GLASS_FRAGMENT_BODY = /* glsl */ `
{
  vec3 glassView = normalize( vViewPosition );
  float ndv = abs( dot( normalize( normal ), glassView ) );
  float fres = pow( 1.0 - ndv, uEdgePower );

  // Cylindrical, matching the shell UVs in ./arena-mesh: the seam is one texel
  // wide and lands on the +Z centreline, behind the blue goal.
  float gu = ( atan( vGlassWorld.x, vGlassWorld.z ) * 0.15915494 + 0.5 ) * uGridU;
  float gv = ( vGlassWorld.y / uGridHeight ) * uGridV;
  float grid = max( glassGridLine( gu ), glassGridLine( gv ) ) * uGridStrength;

  float blueEnd = smoothstep( uEndStart, uEndFull, vGlassWorld.z );
  float orangeEnd = smoothstep( uEndStart, uEndFull, -vGlassWorld.z );
  vec3 endGlow = ( uBlueColor * blueEnd + uOrangeColor * orangeEnd ) * uEndGlow;

  gl_FragColor.rgb += uEdgeColor * fres + uGridColor * grid + endGlow * ( 0.3 + 1.1 * fres );
  gl_FragColor.a = clamp(
    gl_FragColor.a + fres * uEdgeAlpha + grid * 0.7 + ( blueEnd + orangeEnd ) * uEndGlow * 0.35,
    0.0,
    1.0
  );
}
`;

/**
 * Turn a standard/physical material into a pane of stadium glass.
 *
 * The shell has to do two contradictory things: disappear, so the stadium
 * behind it is legible from the chase camera, and exist, so the player can read
 * where the wall is at 2,300 uu/s. A flat 40% alpha does neither well — it is
 * a uniform grey veil that hides the crowd AND gives no edge to aim at.
 *
 * The fix is to put the opacity where the information is. A Fresnel term makes
 * the wall almost invisible where you look straight through it and bright where
 * it curves away, which is exactly where the wall's shape lives; a mullion grid
 * gives parallax and scale; and a team wash at each end tells you which half you
 * are defending from a glance at the wall alone.
 *
 * The uniforms object is returned AND parked on `material.userData.glass`, so
 * the tests can assert which colour landed on which end without a GL context.
 */
export const applyGlassShader = (
  material: MeshStandardMaterial,
  options: GlassOptions = {}
): GlassUniforms => {
  const uniforms: GlassUniforms = {
    uEdgeColor: { value: new Color(options.edgeColor ?? 0x9fc4ff) },
    uEdgePower: { value: options.edgePower ?? 3 },
    uEdgeAlpha: { value: options.edgeAlpha ?? 0.42 },
    uGridColor: { value: new Color(options.gridColor ?? 0x86a8dd) },
    uGridU: { value: options.gridU ?? 64 },
    uGridV: { value: options.gridV ?? 8 },
    uGridHeight: { value: options.gridHeight ?? ARENA.ceiling },
    uGridStrength: { value: options.gridStrength ?? 0.14 },
    uBlueColor: { value: new Color(TEAM_COLORS[0]) },
    uOrangeColor: { value: new Color(TEAM_COLORS[1]) },
    uEndGlow: { value: options.endGlow ?? 0.14 },
    uEndStart: { value: options.endStart ?? 1500 },
    uEndFull: { value: options.endFull ?? ARENA.halfLength },
  };

  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = GLASS_VERTEX_HEAD + shader.vertexShader;
    shader.vertexShader = shader.vertexShader.replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>\n${GLASS_VERTEX_BODY}`
    );
    shader.fragmentShader = GLASS_FRAGMENT_HEAD + shader.fragmentShader;
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <opaque_fragment>',
      `#include <opaque_fragment>\n${GLASS_FRAGMENT_BODY}`
    );
  };
  // Two materials with different uniform values must not share a program.
  material.customProgramCacheKey = () =>
    `glass:${uniforms.uGridU.value}:${uniforms.uGridV.value}:${uniforms.uEndGlow.value}`;
  material.userData.glass = uniforms;
  material.needsUpdate = true;
  return uniforms;
};

/**
 * The ball: a panelled sphere. Rocket League's ball reads as a hex/pent grid
 * with glowing seams, which is exactly what a distance-to-nearest-seed field
 * over a sphere gives you — so the panels are generated as a Voronoi-ish
 * distance field over a fixed point set rather than drawn by hand.
 */
export const makeBallTexture = (): CanvasTexture => {
  const w = 1024;
  const h = 512;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable');

  const image = ctx.createImageData(w, h);
  const seeds = ballPanelSeeds();
  const data = image.data;
  for (let py = 0; py < h; py++) {
    const theta = (py / (h - 1)) * Math.PI;
    const st = Math.sin(theta);
    const ct = Math.cos(theta);
    for (let px = 0; px < w; px++) {
      const phi = (px / w) * Math.PI * 2;
      const x = st * Math.cos(phi);
      const y = st * Math.sin(phi);
      const z = ct;
      // Two nearest seeds; their gap is the seam distance, which is what gives
      // a constant-width line instead of a blob around each seed.
      let best = -2;
      let second = -2;
      for (const s of seeds) {
        const d = x * s[0] + y * s[1] + z * s[2];
        if (d > best) {
          second = best;
          best = d;
        } else if (d > second) {
          second = d;
        }
      }
      const seam = Math.max(0, 1 - (best - second) / 0.055);
      const o = (py * w + px) * 4;
      // Dark panel, bright seam, with a subtle tone break per panel band.
      const panel = 26 + 14 * (0.5 + 0.5 * Math.sin(best * 40));
      data[o] = panel + seam * 210;
      data[o + 1] = panel + seam * 222;
      data[o + 2] = panel + seam * 255;
      data[o + 3] = 255;
    }
  }
  ctx.putImageData(image, 0, 0);

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  texture.anisotropy = 8;
  return texture;
};

/**
 * Panel seed directions: the 12 icosahedron vertices plus the 20 face centres,
 * which is the classic 32-panel football layout (12 pentagons, 20 hexagons).
 * Deterministic — no PRNG involved — so the ball looks identical every run.
 */
export const ballPanelSeeds = (): [number, number, number][] => {
  const phi = (1 + Math.sqrt(5)) / 2;
  const verts: [number, number, number][] = [];
  for (const s1 of [-1, 1]) {
    for (const s2 of [-1, 1]) {
      verts.push([0, s1 * 1, s2 * phi]);
      verts.push([s1 * 1, s2 * phi, 0]);
      verts.push([s2 * phi, 0, s1 * 1]);
    }
  }
  const norm = (v: [number, number, number]): [number, number, number] => {
    const l = Math.hypot(v[0], v[1], v[2]);
    return [v[0] / l, v[1] / l, v[2] / l];
  };
  const corners = verts.map(norm);
  const out = [...corners];

  // Face centres: every triple of vertices that are mutually nearest
  // neighbours. Iterate `corners`, never `out` — appending to the array being
  // scanned is an unbounded loop, and the failure mode is an out-of-memory
  // crash rather than a wrong ball.
  const edge = 2 / Math.sqrt(phi * phi + 1) + 1e-6;
  const d = (a: readonly number[], b: readonly number[]): number =>
    Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
  for (let i = 0; i < corners.length; i++) {
    for (let j = i + 1; j < corners.length; j++) {
      if (d(corners[i], corners[j]) >= edge) continue;
      for (let k = j + 1; k < corners.length; k++) {
        if (d(corners[j], corners[k]) < edge && d(corners[i], corners[k]) < edge) {
          out.push(
            norm([
              corners[i][0] + corners[j][0] + corners[k][0],
              corners[i][1] + corners[j][1] + corners[k][1],
              corners[i][2] + corners[j][2] + corners[k][2],
            ])
          );
        }
      }
    }
  }
  return out;
};

// ---------------------------------------------------------------------------
// Particle sprite
// ---------------------------------------------------------------------------

/**
 * RGBA bytes for a soft round particle: a radial falloff raised to `power`.
 * Pure, so the falloff can be asserted without a GPU. Alpha is the falloff and
 * RGB is white, because every particle system here tints via vertex colour.
 */
export const particleSpriteData = (size = 64, power = 2.2): Uint8Array => {
  const data = new Uint8Array(size * size * 4);
  const c = (size - 1) / 2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const d = Math.hypot(x - c, y - c) / c;
      const a = d >= 1 ? 0 : Math.pow(1 - d, power);
      const o = (y * size + x) * 4;
      data[o] = 255;
      data[o + 1] = 255;
      data[o + 2] = 255;
      data[o + 3] = Math.round(a * 255);
    }
  }
  return data;
};

export const makeParticleTexture = (size = 64): DataTexture => {
  const texture = new DataTexture(particleSpriteData(size), size, size, RGBAFormat);
  texture.needsUpdate = true;
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  return texture;
};

// ---------------------------------------------------------------------------
// Environment map
// ---------------------------------------------------------------------------

/**
 * Equirectangular RGBA float data for the environment map: dark sky at the
 * zenith, a bright warm band at the stadium-light height, cool bounce from the
 * pitch below. PBR materials need SOMETHING to reflect — with no environment
 * term the car paint goes flat and matte and no amount of light tuning fixes it.
 *
 * Pure and testable: the brightness profile is the whole point, and it is a
 * function of latitude only plus four light blobs.
 */
export const equirectEnvData = (width = 128, height = 64): Float32Array => {
  const data = new Float32Array(width * height * 4);
  for (let y = 0; y < height; y++) {
    // v = 0 at the top of the image = zenith.
    const v = (y + 0.5) / height;
    const theta = v * Math.PI;
    const up = Math.cos(theta); // +1 zenith, -1 nadir

    // NIGHT. The zenith is open sky and is nearly black; almost every photon
    // in this map comes from the four light banks and from the bowl bouncing
    // them back. That contrast is the point — an evenly lit environment map
    // makes clearcoat look like matte plastic no matter what the lights do.
    const horizonBand = Math.pow(Math.max(0, 1 - Math.abs(up)), 8);
    let r = 0.006 + horizonBand * 0.10;
    let g = 0.009 + horizonBand * 0.11;
    let b = 0.020 + horizonBand * 0.15;

    // The stands: a broad, dim, slightly warm mass filling the lower half.
    // This is what a car sees reflected in its flanks for most of a match.
    if (up < 0.18) {
      const t = Math.min(1, (0.18 - up) / 0.7);
      r += 0.055 * t;
      g += 0.052 * t;
      b += 0.058 * t;
    }
    // Bounce off the pitch, at the nadir and tinted by the floor colour.
    if (up < -0.35) {
      const t = (-up - 0.35) / 0.65;
      r += 0.030 * t;
      g += 0.042 * t;
      b += 0.075 * t;
    }

    for (let x = 0; x < width; x++) {
      const u = (x + 0.5) / width;
      const phi = u * Math.PI * 2;

      // Four floodlight banks, at the arena's corner planes — the same four
      // the stadium builds masts for and the field texture bakes pools from.
      let flood = 0;
      for (let i = 0; i < 4; i++) {
        const lightPhi = Math.PI / 4 + (i * Math.PI) / 2;
        let d = Math.abs(phi - lightPhi);
        if (d > Math.PI) d = Math.PI * 2 - d;
        const lat = Math.abs(up - 0.42);
        flood += Math.exp(-(d * d) / 0.05 - (lat * lat) / 0.012);
      }
      flood *= 4.4;

      // A dimmer, wider ring of roof lighting just above the crowd, so the
      // reflection in a car flank sweeps continuously instead of strobing
      // between four hard blobs.
      const ringLat = Math.abs(up - 0.16);
      const ring = Math.exp(-(ringLat * ringLat) / 0.004) * 0.30;

      const o = (y * width + x) * 4;
      data[o] = r + flood * 1.0 + ring * 1.0;
      data[o + 1] = g + flood * 0.95 + ring * 0.96;
      data[o + 2] = b + flood * 0.84 + ring * 0.9;
      data[o + 3] = 1;
    }
  }
  return data;
};

export const makeEnvironmentTexture = (width = 256, height = 128): DataTexture => {
  const texture = new DataTexture(
    equirectEnvData(width, height),
    width,
    height,
    RGBAFormat,
    FloatType
  );
  texture.mapping = EquirectangularReflectionMapping;
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  texture.needsUpdate = true;
  return texture;
};

// ---------------------------------------------------------------------------
// Materials
// ---------------------------------------------------------------------------

export interface ArenaMaterials {
  floor: MeshStandardMaterial;
  /** Floor-to-wall fillets. Opaque, so the edge of the pitch stays readable. */
  ramp: MeshStandardMaterial;
  wall: MeshPhysicalMaterial;
  ceiling: MeshPhysicalMaterial;
  goalInterior: [MeshStandardMaterial, MeshStandardMaterial];
  trim: MeshStandardMaterial;
  net: [LineBasicMaterial, LineBasicMaterial];
  /** Live uniforms for the two glass surfaces; see `applyGlassShader`. */
  glass: { wall: GlassUniforms; ceiling: GlassUniforms };
  dispose(): void;
}

/**
 * The arena material set.
 *
 * THE FLOOR IS OPAQUE AND THE SHELL IS NOT. That split is the whole art
 * direction: the pitch is the play surface and has to be solid and readable,
 * and everything above it is glass so the stadium — the crowd, the LED ring,
 * the roof, the city — is the backdrop of every shot. See `applyGlassShader`
 * for why the glass is a Fresnel rim and a grid rather than flat alpha.
 */
export const makeArenaMaterials = (): ArenaMaterials => {
  const fieldTexture = makeFieldTexture();

  // LOW metalness and high roughness. At 0.16/0.5 the pitch grew a single
  // enormous specular sheet from the key light that slid around with the
  // camera and flattened every marking under it. A football pitch is a diffuse
  // surface and has to be lit like one.
  const floor = new MeshStandardMaterial({
    map: fieldTexture,
    color: 0xffffff,
    roughness: 0.74,
    metalness: 0.04,
    envMapIntensity: 0.38,
  });

  // The fillets carry the pitch texture straight up out of the floor (they use
  // the same planar UV, see `buildArenaSurfaces`), so the field does not stop
  // dead at a grey skirting board.
  const ramp = new MeshStandardMaterial({
    map: fieldTexture,
    color: 0x4b566b,
    roughness: 0.35,
    metalness: 0.55,
    envMapIntensity: 1.1,
  });

  const wall = new MeshPhysicalMaterial({
    color: PALETTE.wall,
    roughness: 0.12,
    metalness: 0.2,
    transparent: true,
    // Low, because the Fresnel term in the glass shader adds the rest exactly
    // where it is wanted. Raising this instead is what makes a grey veil.
    opacity: 0.13,
    // FRONT side, not double. The collision mesh is wound so its normals point
    // INTO the field, so front-facing == seen from inside: the shell draws for
    // a camera in the arena and vanishes for one that has swung outside it,
    // which is exactly the behaviour the chase camera needs behind a goal.
    // Drawing both sides paints a giant washed-out plane over the whole view
    // the moment the camera clips through a wall.
    side: FrontSide,
    envMapIntensity: 1.15,
    depthWrite: false,
  });

  // Fainter still, and with a much coarser grid: the ceiling is meant to read
  // as open air with a light rig above it, not as a lid.
  const ceiling = new MeshPhysicalMaterial({
    color: PALETTE.ceiling,
    roughness: 0.1,
    metalness: 0.3,
    transparent: true,
    opacity: 0.05,
    side: FrontSide,
    envMapIntensity: 0.9,
    depthWrite: false,
  });

  const glass = {
    wall: applyGlassShader(wall, {
      edgeColor: 0x9cc2ff,
      edgePower: 3,
      edgeAlpha: 0.5,
      gridU: 72,
      gridV: 8,
      gridStrength: 0.16,
      endGlow: 0.16,
    }),
    ceiling: applyGlassShader(ceiling, {
      edgeColor: 0x7fa4e0,
      edgePower: 4,
      edgeAlpha: 0.22,
      gridU: 40,
      gridV: 3,
      gridStrength: 0.07,
      endGlow: 0.05,
    }),
  };

  const goalInterior = ([0, 1] as const).map(
    (team) =>
      new MeshStandardMaterial({
        color: TEAM_COLORS_DIM[team],
        emissive: TEAM_COLORS[team],
        emissiveIntensity: 0.35,
        roughness: 0.7,
        metalness: 0.1,
        side: DoubleSide,
      })
  ) as [MeshStandardMaterial, MeshStandardMaterial];

  const trim = new MeshStandardMaterial({
    color: PALETTE.trim,
    roughness: 0.3,
    metalness: 0.85,
    envMapIntensity: 1.2,
  });

  const net = ([0, 1] as const).map(
    (team) =>
      new LineBasicMaterial({
        color: TEAM_COLORS[team],
        transparent: true,
        opacity: 0.38,
      })
  ) as [LineBasicMaterial, LineBasicMaterial];

  return {
    floor,
    ramp,
    wall,
    ceiling,
    goalInterior,
    trim,
    net,
    glass,
    dispose() {
      fieldTexture.dispose();
      floor.dispose();
      ramp.dispose();
      wall.dispose();
      ceiling.dispose();
      trim.dispose();
      for (const m of goalInterior) m.dispose();
      for (const m of net) m.dispose();
    },
  };
};

// ---------------------------------------------------------------------------
// Car materials
// ---------------------------------------------------------------------------

/**
 * Shut lines around the body, in the loft's own UV space (see
 * `buildBodyGeometry`): v runs nose -> tail, u runs around the cross-section
 * from the underbody centreline. So a constant-v line is a TRANSVERSE panel gap
 * — bumper seam, hood, door, deck — and a constant-u line is a longitudinal
 * crease down a flank.
 */
const PANEL_LINES_V: readonly number[] = [0.13, 0.34, 0.53, 0.78, 0.9];
const PANEL_LINES_U: readonly number[] = [0.2, 0.8];

/** Deterministic value noise. No PRNG state, so the flake never moves. */
const hashNoise = (x: number, y: number): number => {
  const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return s - Math.floor(s);
};

/** Triangular falloff toward `at`, wrapping in [0, 1). */
const lineFalloff = (t: number, at: number, width: number): number => {
  let d = Math.abs(t - at);
  if (d > 0.5) d = 1 - d;
  return d >= width ? 0 : 1 - d / width;
};

/**
 * Car paint detail, as RGBA bytes: metallic flake plus panel shut lines,
 * written into the green channel because that is the channel Three's
 * `roughnessMap` and `clearcoatRoughnessMap` sample.
 *
 * Pure and deterministic — no canvas, no `Math.random` — so it runs in Node
 * and the same car comes out of every run. The whole point of it is that a
 * perfectly uniform clearcoat reads as plastic: the arena's four floodlights
 * sweep across a car as it drives, and they need something to break up.
 */
export const carDetailData = (size = 256): Uint8Array => {
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    const v = (y + 0.5) / size;
    for (let x = 0; x < size; x++) {
      const u = (x + 0.5) / size;
      // Base is well under 1 so the material's own roughness is the ceiling.
      let rough = 0.4 + 0.07 * hashNoise(x, y);
      for (const line of PANEL_LINES_V) rough += 0.9 * lineFalloff(v, line, 0.008);
      for (const line of PANEL_LINES_U) rough += 0.5 * lineFalloff(u, line, 0.006);
      const b = Math.round(255 * Math.min(1, rough));
      const o = (y * size + x) * 4;
      data[o] = b;
      data[o + 1] = b;
      data[o + 2] = b;
      data[o + 3] = 255;
    }
  }
  return data;
};

/**
 * One shared detail texture for every car on the field. Built lazily — never at
 * module scope — and never disposed, because it is 256 KB that outlives any
 * individual match and rebuilding it per rematch would be the only cost.
 */
let carDetailTexture: DataTexture | null = null;

export const sharedCarDetailTexture = (size = 256): DataTexture => {
  if (carDetailTexture) return carDetailTexture;
  const texture = new DataTexture(carDetailData(size), size, size, RGBAFormat);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  texture.needsUpdate = true;
  carDetailTexture = texture;
  return texture;
};

// ---------------------------------------------------------------------------
// Paint finishes
// ---------------------------------------------------------------------------

/**
 * A paint finish, as MATERIAL PARAMETERS rather than as a tint.
 *
 * That is the whole point of the slot: a matte car and a metallic car in the
 * same colour must answer a floodlight differently, or "finish" is a word on a
 * menu row. So each one moves metalness, roughness, the clearcoat and the
 * environment response together; pearlescent turns on Three's thin-film
 * `iridescence` (which is what a pearl coat physically IS — flake with an
 * interference layer over it); and carbon swaps the roughness map for a woven
 * one, because a weave is a texture and cannot be faked with a scalar.
 *
 * `map` is which shared texture modulates roughness. A matte finish scatters
 * the light that would otherwise pick the panel gaps out, so dropping the map
 * there is not a cheat: it is the same reason a matte car hides its bodywork
 * faults.
 */
export interface PaintFinish {
  metalness: number;
  roughness: number;
  clearcoat: number;
  clearcoatRoughness: number;
  envMapIntensity: number;
  /** Thin-film interference. Zero for everything but a pearl coat. */
  iridescence: number;
  iridescenceIOR: number;
  iridescenceThickness: readonly [number, number];
  /** Retro-reflective sheen, which is what makes a matte panel look powdery. */
  sheen: number;
  sheenRoughness: number;
  /** Which shared texture drives roughness: the flake, the weave, or nothing. */
  map: 'detail' | 'weave' | 'none';
  /** Tiles of the weave across the body. Ignored unless `map` is 'weave'. */
  weaveRepeat: number;
  /**
   * How far the paint colour is pulled toward white before it is used. Chrome is
   * a mirror, and a mirror is not the colour of its own paint: pulling most of
   * the way to white is what turns a red car into a red-tinted chrome one rather
   * than into a shiny red one.
   */
  desaturate: number;
}

const BASE_FINISH: PaintFinish = {
  metalness: 0.28,
  roughness: 0.5,
  clearcoat: 1,
  clearcoatRoughness: 0.06,
  envMapIntensity: 1.3,
  iridescence: 0,
  iridescenceIOR: 1.3,
  iridescenceThickness: [100, 400],
  sheen: 0,
  sheenRoughness: 1,
  map: 'detail',
  weaveRepeat: 1,
  desaturate: 0,
};

/**
 * The catalogue's paint finishes, by generator name.
 *
 * Seven, and every one of them moves several parameters together, because that
 * is what a finish IS. The two that carry the most work are the two that cannot
 * be faked with a scalar: pearlescent turns on thin-film interference, so its
 * colour shifts with the viewing angle rather than merely being shiny; carbon
 * swaps the flake map for a woven one, because a weave is a texture.
 */
export const PAINT_FINISHES: Readonly<Record<string, PaintFinish>> = Object.freeze({
  /** The stock look: a hard clearcoat over a lightly flaked dielectric. */
  gloss: { ...BASE_FINISH },

  /**
   * Half a clearcoat, and a rougher base under it — the coat is what was
   * smoothing that base, so thinning it has to do both. Reads as a well-kept
   * older car rather than a show car.
   */
  satin: {
    ...BASE_FINISH,
    roughness: 0.62,
    clearcoat: 0.45,
    clearcoatRoughness: 0.28,
    envMapIntensity: 0.95,
    metalness: 0.2,
    sheen: 0.15,
    sheenRoughness: 0.7,
  },

  /**
   * No clearcoat worth the name and no metal at all: the highlight becomes a
   * broad soft wash and the car's SHAPE, not its reflections, is what reads.
   * The flake map goes with it — a matte car hides its bodywork faults.
   */
  matte: {
    ...BASE_FINISH,
    metalness: 0,
    roughness: 0.88,
    clearcoat: 0.05,
    clearcoatRoughness: 0.8,
    envMapIntensity: 0.42,
    sheen: 0.45,
    sheenRoughness: 0.85,
    map: 'none',
  },

  /** Nearly a metal: tight highlight, dark where the environment is dark. */
  metallic: {
    ...BASE_FINISH,
    metalness: 0.88,
    roughness: 0.28,
    clearcoat: 0.6,
    clearcoatRoughness: 0.15,
    envMapIntensity: 1.75,
  },

  /**
   * A pearl: a thin interference film over a smooth base, so the colour SHIFTS
   * with the viewing angle instead of merely being shiny. The one finish whose
   * difference survives a still screenshot.
   */
  pearlescent: {
    ...BASE_FINISH,
    metalness: 0.35,
    roughness: 0.2,
    clearcoatRoughness: 0.03,
    envMapIntensity: 2,
    iridescence: 1,
    iridescenceIOR: 1.8,
    iridescenceThickness: [260, 780],
    sheen: 0.2,
    sheenRoughness: 0.4,
  },

  /**
   * Carbon fibre: a dark base under a hard coat, with a real 2x2 twill in the
   * roughness. Nearly black however it is painted — the paint tints the sheen
   * rather than the cloth, which is what carbon actually does.
   */
  carbon: {
    ...BASE_FINISH,
    metalness: 0.45,
    roughness: 0.42,
    clearcoatRoughness: 0.04,
    envMapIntensity: 1.5,
    map: 'weave',
    weaveRepeat: 22,
    desaturate: -0.55,
  },

  /** A mirror, and a mirror is not the colour of its own paint. */
  chrome: {
    ...BASE_FINISH,
    metalness: 1,
    roughness: 0.03,
    clearcoat: 0,
    clearcoatRoughness: 0,
    envMapIntensity: 3,
    map: 'none',
    desaturate: 0.55,
  },
});

export const PAINT_FINISH_NAMES: readonly string[] = Object.freeze(
  Object.keys(PAINT_FINISHES).sort()
);

/** The stock finish, and the fallback for a loadout with the slot empty. */
export const defaultFinish = (): PaintFinish => PAINT_FINISHES.gloss;

// ---------------------------------------------------------------------------
// Carbon weave
// ---------------------------------------------------------------------------

/**
 * A 2x2 twill weave, as RGBA bytes, written into all three channels because it
 * is used as a roughness map.
 *
 * Pure and deterministic like `carDetailData`, and for the same reasons. The
 * twill — over two, under two, stepping one each row — is what distinguishes
 * carbon fibre from a checkerboard; a plain weave at this scale reads as a
 * gingham tablecloth.
 */
export const carbonWeaveData = (size = 128): Uint8Array => {
  const data = new Uint8Array(size * size * 4);
  const cells = 8;
  for (let y = 0; y < size; y++) {
    const v = (y / size) * cells;
    for (let x = 0; x < size; x++) {
      const u = (x / size) * cells;
      const cx = Math.floor(u);
      const cy = Math.floor(v);
      // 2x2 twill: the float direction steps one cell per row.
      const warpUp = (((cx - cy) % 4) + 4) % 4 < 2;
      const fu = u - cx;
      const fv = v - cy;
      // A tow is a rounded ridge across its own width, which is what catches the
      // light band that makes a weave legible at all.
      const across = warpUp ? fu : fv;
      const ridge = Math.sin(across * Math.PI);
      const rough = 0.28 + 0.5 * (1 - ridge * ridge) + 0.05 * (warpUp ? 1 : 0);
      const b = Math.round(255 * Math.min(1, rough));
      const o = (y * size + x) * 4;
      data[o] = b;
      data[o + 1] = b;
      data[o + 2] = b;
      data[o + 3] = 255;
    }
  }
  return data;
};

let carbonTexture: DataTexture | null = null;
const carbonTiles = new Map<number, Texture>();

/**
 * One weave for every carbon car on the field, at a given tiling.
 *
 * The tiling has to live on the TEXTURE (`repeat` is a texture property, not a
 * material one), so a second tow width needs a second texture object — but
 * `clone` shares the pixel data, so the extra one costs a descriptor and not
 * another 64 KB. Built lazily and never disposed, like the flake map.
 */
export const sharedCarbonWeaveTexture = (repeat = 16, size = 128): Texture => {
  if (!carbonTexture) {
    carbonTexture = new DataTexture(carbonWeaveData(size), size, size, RGBAFormat);
    carbonTexture.wrapS = RepeatWrapping;
    carbonTexture.wrapT = RepeatWrapping;
    carbonTexture.minFilter = LinearFilter;
    carbonTexture.magFilter = LinearFilter;
    carbonTexture.needsUpdate = true;
  }
  const tiles = Math.max(1, Math.round(repeat));
  const cached = carbonTiles.get(tiles);
  if (cached) return cached;
  const texture = carbonTexture.clone();
  texture.repeat.set(tiles, tiles);
  texture.needsUpdate = true;
  carbonTiles.set(tiles, texture);
  return texture;
};

/**
 * Car paint: a clearcoat over a lightly metallic base.
 *
 * `metalness` deliberately low on the stock finish. Real automotive paint is a
 * dielectric with metal flake suspended in it, not a metal — push metalness up
 * and the car stops looking painted and starts looking like it was machined out
 * of a billet of team colour, which is exactly what the first pass did. The
 * clearcoat provides the hard highlight and `roughnessMap` provides the flake.
 * The `metallic` finish above pushes it up ON PURPOSE, which is a different
 * claim: that item is supposed to look machined.
 *
 * With a livery map the material's own `color` goes WHITE and the map carries
 * the paint. It has to: the map is absolute colour (see ./decals), so a decal
 * could not be a different colour from the body if the body colour multiplied
 * through it.
 */
export const makeCarPaint = (
  team: 0 | 1,
  options: CarMaterialOptions = {}
): MeshPhysicalMaterial => {
  const finish = options.finish ?? defaultFinish();
  // A mirror is not the colour of its own paint, and carbon is nearly black
  // however it is painted. One number covers both, in either direction.
  const base = new Color(options.skin ? 0xffffff : (options.paint ?? TEAM_COLORS[team]));
  if (finish.desaturate > 0) base.lerp(new Color(0xffffff), finish.desaturate);
  else if (finish.desaturate < 0) base.lerp(new Color(0x000000), -finish.desaturate);

  const material = new MeshPhysicalMaterial({
    color: base,
    map: options.skin ?? null,
    metalness: finish.metalness,
    roughness: finish.roughness,
    clearcoat: finish.clearcoat,
    clearcoatRoughness: finish.clearcoatRoughness,
    envMapIntensity: finish.envMapIntensity,
  });
  // The roughness map is a modulation, so a finish that does not want one simply
  // does not take it rather than taking a washed-out copy. Carbon takes a
  // different map entirely: a weave is a texture and no scalar approximates it.
  if (finish.map === 'detail') {
    material.roughnessMap = sharedCarDetailTexture();
    material.clearcoatRoughnessMap = sharedCarDetailTexture();
  } else if (finish.map === 'weave') {
    const weave = sharedCarbonWeaveTexture(finish.weaveRepeat);
    material.roughnessMap = weave;
    material.clearcoatRoughnessMap = weave;
  }
  if (finish.iridescence > 0) {
    material.iridescence = finish.iridescence;
    material.iridescenceIOR = finish.iridescenceIOR;
    material.iridescenceThicknessRange = [
      finish.iridescenceThickness[0],
      finish.iridescenceThickness[1],
    ];
  }
  if (finish.sheen > 0) {
    material.sheen = finish.sheen;
    material.sheenRoughness = finish.sheenRoughness;
    material.sheenColor = new Color(0xffffff);
  }
  return material;
};

/**
 * Everything a car is made of, in one object.
 *
 * The split is by how a surface answers a light, not by where it is on the car:
 * paint takes a clearcoat and reflects the floodlights, the underbody eats
 * them, tyres are matte, rims are polished, glass is dark and mirror-like, and
 * the lamps are emissive so the car still reads as a car when it is in the
 * shadow of the goal frame.
 */
export interface CarMaterials {
  paint: MeshPhysicalMaterial;
  /** Floor pan and inner arches. Never painted, never lit. */
  underbody: MeshStandardMaterial;
  /** Splitter, diffuser, skirts, wing, mirrors — dark satin metal. */
  trim: MeshStandardMaterial;
  glass: MeshPhysicalMaterial;
  tyre: MeshStandardMaterial;
  rim: MeshStandardMaterial;
  rimDark: MeshStandardMaterial;
  /** Team-coloured emissive: the tell that survives a chase camera at distance. */
  accent: MeshStandardMaterial;
  /**
   * Painted parts of a topper or an antenna. Its own material rather than the
   * body paint because an attachment takes the ITEM's paint, not the car's — a
   * Titanium White cone on a black car is the normal case, not the odd one.
   */
  attachment: MeshStandardMaterial;
  headlight: MeshStandardMaterial;
  taillight: MeshStandardMaterial;
  aura: MeshStandardMaterial;
  all(): readonly (MeshStandardMaterial | MeshPhysicalMaterial)[];
  dispose(): void;
}

/**
 * What a livery overrides on a car's material set.
 *
 * Every field is optional and every default is the stock team car, so
 * `makeCarMaterials(team)` with no options is exactly what it always was — the
 * customization path is additive, and a car with no loadout is not routed
 * through it.
 */
export interface CarMaterialOptions {
  /** The absolute-colour body map from ./livery. Sends the paint colour white. */
  skin?: Texture | null;
  /** Resolved through `FINISH_GENERATORS`. Defaults to gloss. */
  finish?: PaintFinish;
  /** Base paint colour when there is no skin map. */
  paint?: number;
  /** Rim face colour. Defaults to polished chrome. */
  rim?: number;
  /** Painted parts of toppers and antennas. */
  attachment?: number;
}

export const makeCarMaterials = (
  team: 0 | 1,
  options: CarMaterialOptions = {}
): CarMaterials => {
  const paint = makeCarPaint(team, options);
  // The emissive accents stay the TEAM colour whatever the loadout says: they
  // are the one thing on the car that is information rather than decoration, and
  // a player who repaints them blue on the orange team has broken the game's
  // most-used read.
  const accentColor = TEAM_COLORS[team];

  const underbody = new MeshStandardMaterial({
    color: 0x0b0d12,
    roughness: 0.95,
    metalness: 0.1,
    envMapIntensity: 0.25,
  });

  const trim = new MeshStandardMaterial({
    color: 0x22262e,
    roughness: 0.42,
    metalness: 0.9,
    envMapIntensity: 1,
  });

  // TINT PLUS REFLECTION, not absorption. The first pass was a near-black base
  // with almost no transmission, which is a hole in the car rather than a
  // window: nothing came through it and there was too little in the environment
  // for it to reflect, so in profile the whole cabin read as a void and hid the
  // shape lofted underneath.
  //
  // So: a base bright enough to be a colour, half the light let through, the
  // attenuation doing the tinting over `thickness` (that is what makes glass
  // look thick without making it dark), and a hard specular on top. The cost of
  // transmission on six cars is one framebuffer copy per frame, which is the
  // right trade for the one surface on the car that is supposed to be the
  // brightest thing on it when a floodlight crosses it.
  const glass = new MeshPhysicalMaterial({
    color: PALETTE.glass,
    roughness: 0.03,
    metalness: 0.1,
    transmission: 0.72,
    thickness: 5,
    ior: 1.45,
    attenuationColor: new Color(0x243a5c),
    attenuationDistance: 12,
    specularIntensity: 1,
    envMapIntensity: 3.2,
    clearcoat: 1,
    clearcoatRoughness: 0.02,
  });

  const tyre = new MeshStandardMaterial({
    color: PALETTE.rubber,
    roughness: 0.96,
    metalness: 0.02,
    envMapIntensity: 0.3,
  });

  const rim = new MeshStandardMaterial({
    color: options.rim ?? PALETTE.chrome,
    roughness: 0.18,
    metalness: 1,
    envMapIntensity: 1.8,
  });

  const rimDark = new MeshStandardMaterial({
    color: 0x30363f,
    roughness: 0.45,
    metalness: 1,
    envMapIntensity: 0.9,
  });

  const accent = new MeshStandardMaterial({
    color: accentColor,
    emissive: accentColor,
    emissiveIntensity: 0.85,
    roughness: 0.35,
    metalness: 0.2,
  });

  // A clearcoated painted plastic, one step glossier than the body: an
  // attachment is a small object seen at close range in the garage, and matching
  // the body's finish would make a matte car's topper disappear into it.
  const attachment = new MeshStandardMaterial({
    color: options.attachment ?? PALETTE.chrome,
    roughness: 0.34,
    metalness: 0.25,
    envMapIntensity: 1.25,
  });

  const headlight = new MeshStandardMaterial({
    color: 0xdfefff,
    emissive: 0xcfe6ff,
    emissiveIntensity: 2.2,
    roughness: 0.1,
    metalness: 0.1,
  });

  const taillight = new MeshStandardMaterial({
    color: 0x8c1207,
    emissive: 0xff2a12,
    emissiveIntensity: 1.9,
    roughness: 0.2,
    metalness: 0.1,
  });

  const aura = new MeshStandardMaterial({
    color: 0xffffff,
    emissive: 0x9fd0ff,
    emissiveIntensity: 2.2,
    transparent: true,
    opacity: 0.16,
    depthWrite: false,
  });

  const all = (): readonly (MeshStandardMaterial | MeshPhysicalMaterial)[] => [
    paint,
    underbody,
    trim,
    glass,
    tyre,
    rim,
    rimDark,
    accent,
    attachment,
    headlight,
    taillight,
    aura,
  ];

  return {
    paint,
    underbody,
    trim,
    glass,
    tyre,
    rim,
    rimDark,
    accent,
    attachment,
    headlight,
    taillight,
    aura,
    all,
    dispose() {
      // The detail texture is shared and deliberately outlives this set.
      for (const m of all()) m.dispose();
    },
  };
};

export const disposeTexture = (t: Texture | null | undefined): void => {
  if (t) t.dispose();
};
